import React from 'react';
import { Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { OrderSettlementAdminTaskDto, OrderSettlementCommandState } from '../../../dto/orderSettlement';

const { Text } = Typography;

interface Props {
  state: OrderSettlementCommandState | null | undefined;
  /** Amount-free handoff shown beside the ADMIN_REQUIRED tag. */
  adminTask?: OrderSettlementAdminTaskDto | null;
}

const tagStyle = { borderRadius: 999, paddingInline: 10, fontWeight: 600 };

/** Short human reference for the task deep link; the id itself stays technical. */
export function settlementTaskReference(taskId: string | null | undefined): string {
  return (taskId || '').slice(0, 6).toUpperCase();
}

/** A deliberately coarse command result suitable for every permitted role. */
export const OrderSettlementCommandStatus: React.FC<Props> = ({ state, adminTask }) => {
  const { t } = useTranslation('orders');

  if (state === 'ADMIN_REQUIRED') {
    const taskId = adminTask?.taskId || null;
    const categories = adminTask?.categories || [];
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Tag color="warning" style={tagStyle}>
            {taskId
              ? t('orderDetail.settlementCommandHandedOver', { task: settlementTaskReference(taskId) })
              : t('orderDetail.settlementCommandAdminRequired')}
          </Tag>
          {taskId && (
            <Link to={`/app/tasks?task=${encodeURIComponent(taskId)}`}>
              {t('orderDetail.settlementCommandOpenTask')}
            </Link>
          )}
        </span>
        {categories.length > 0 && (
          <Text type="secondary" data-testid="settlement-admin-task-categories">
            {t('orderDetail.settlementCommandMissingData')}: {categories.map((category) => category.label).join('; ')}
          </Text>
        )}
      </span>
    );
  }

  if (state === 'APPLIED') {
    return (
      <Tag color="success" style={tagStyle}>
        {t('orderDetail.settlementCommandApplied')}
      </Tag>
    );
  }

  if (state === 'SUPERSEDED') {
    return (
      <Tag color="default" style={tagStyle}>
        {t('orderDetail.settlementCommandSuperseded')}
      </Tag>
    );
  }

  return null;
};
